import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { learningCollectionsStore } from '../../../../../../../stores/LearningCollectionsStore';
import { T } from '../../../../../../../Translations';
import { LearningCollectionDetailsProvider } from '../../../../../../LearningCollectionDetailsContext';
import { SectionSkeleton } from '../../Skeletons';
import Section from '../../Section';
import AddSections from '../../Section/AddSections';
import { UnifiedDragProvider } from '../../Section/DisplayTypes/UnifiedDragContext';
import SaveActionsStore from '../../ReadyBuilder/BuilderV2SaveActions/Store';
import { ProductCollectionResourceArray } from '../../Types';
import { useNewSectionsLoader } from './useNewSectionsLoader';

interface Props {
  product_collections: ProductCollectionResourceArray;
}

function SectionsLoader({ product_collections }: Props) {
  const [store] = useState(() => new SaveActionsStore());
  const {
    handleNewProductCollectionChange,
    handleAddBrowseAllSection,
    handleAddCuratedSections,
    canAddBrowseAll,
  } = useNewSectionsLoader(store);

  const totalSections = product_collections.length;
  const storeState = learningCollectionsStore.state;

  // Get experiencesArray when available, empty array as fallback
  const experiencesArray =
    storeState.kind === 'ready' ? storeState.data.resource.payload.experiences : [];

  // Track when sections are added and scroll to the newly added section
  const prevSectionCountRef = useRef(totalSections);
  const scrollToIndexRef = useRef<number | null>(null);
  const timeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Scroll to newly added section
  useEffect(() => {
    const prevCount = prevSectionCountRef.current;
    const currentCount = totalSections;

    // Section was added and we know which index to scroll to
    if (currentCount > prevCount && scrollToIndexRef.current !== null) {
      const scrollToIndex = scrollToIndexRef.current;

      // For first section (index 0), wait for success banner to appear, then scroll to it
      if (scrollToIndex === 0) {
        let attempts = 0;
        const maxAttempts = 20; // Try for up to 2 seconds (20 * 100ms)

        const tryScrollToBanner = () => {
          const bannerElement = document.querySelector('[data-success-banner]') as HTMLElement;
          if (bannerElement) {
            bannerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            scrollToIndexRef.current = null;
          } else if (attempts < maxAttempts) {
            attempts++;
            const timeoutId = setTimeout(tryScrollToBanner, 100);
            timeoutIdsRef.current.add(timeoutId);
          } else {
            // Fallback: scroll to the section itself if banner never appears
            const sectionElement = document.querySelector(
              `[data-section-index="0"]`,
            ) as HTMLElement;
            if (sectionElement) {
              sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            scrollToIndexRef.current = null;
          }
        };

        const initialTimeoutId = setTimeout(tryScrollToBanner, 100);
        timeoutIdsRef.current.add(initialTimeoutId);
      } else {
        // For other sections, scroll to center to show full section
        const timeoutId = setTimeout(() => {
          const sectionElement = document.querySelector(
            `[data-section-index="${scrollToIndex}"]`,
          ) as HTMLElement;

          if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          scrollToIndexRef.current = null;
        }, 100);
        timeoutIdsRef.current.add(timeoutId);
      }
    }

    prevSectionCountRef.current = currentCount;

    // Cleanup: clear all pending timeouts on unmount
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timeoutIds = timeoutIdsRef.current;
      timeoutIds.forEach((id) => clearTimeout(id));
      timeoutIds.clear();
    };
  }, [totalSections]);

  // Wrapped handlers that track the insertion index for scrolling
  const handleSelectBlankWithScroll = (insertionIndex?: number) => {
    // When insertionIndex is undefined, it means adding to empty collection (index 0)
    scrollToIndexRef.current = insertionIndex !== undefined ? insertionIndex : 0;
    handleNewProductCollectionChange(insertionIndex);
  };

  const handleSelectBrowseAllWithScroll = (insertionIndex?: number) => {
    // When insertionIndex is undefined, it means adding to empty collection (index 0)
    scrollToIndexRef.current = insertionIndex !== undefined ? insertionIndex : 0;
    handleAddBrowseAllSection(insertionIndex);
  };

  const handleSelectCuratedWithScroll = (curatedSectionIds: number[], insertionIndex?: number) => {
    // When insertionIndex is undefined, it means adding to empty collection (index 0)
    scrollToIndexRef.current = insertionIndex !== undefined ? insertionIndex : 0;
    handleAddCuratedSections(curatedSectionIds, insertionIndex);
  };

  // Empty state - show immediately without waiting for learningCollectionsStore
  // experiencesArray will be populated when store is ready (for CuratedSectionsModal)
  if (totalSections === 0) {
    return (
      <AddSections
        onSelectBlank={handleSelectBlankWithScroll}
        onSelectBrowseAll={handleSelectBrowseAllWithScroll}
        onSelectCurated={handleSelectCuratedWithScroll}
        canAddBrowseAll={canAddBrowseAll}
        experiencesArray={experiencesArray}
        isEmpty
      />
    );
  }

  // When there are sections, we need to wait for learningCollectionsStore data
  switch (storeState.kind) {
    case 'errored':
      return <T kind="Something went wrong" />;
    case 'loading':
      return <SectionSkeleton />;
    case 'ready':
      return (
        <LearningCollectionDetailsProvider resource={storeState.data.resource}>
          <div className="flex flex-col">
            {/* Insertion zone before all sections - reduced top padding to match spacing below */}
            <div className="-mt-8">
              <AddSections
                onSelectBlank={handleSelectBlankWithScroll}
                onSelectBrowseAll={handleSelectBrowseAllWithScroll}
                onSelectCurated={handleSelectCuratedWithScroll}
                canAddBrowseAll={canAddBrowseAll}
                experiencesArray={experiencesArray}
                insertionIndex={0}
              />
            </div>

            <UnifiedDragProvider>
              <div className="flex flex-grow flex-col">
                {product_collections.map((product_collection, index) => (
                  <div
                    key={product_collection.payload.id.getOrElseValue(index)}
                    data-section-index={index}
                  >
                    <Section
                      index={index}
                      totalSections={totalSections}
                      productCollection={product_collection}
                      experiencesArray={experiencesArray}
                      editable
                    />
                    {/* Insertion zone after each section */}
                    <AddSections
                      onSelectBlank={handleSelectBlankWithScroll}
                      onSelectBrowseAll={handleSelectBrowseAllWithScroll}
                      onSelectCurated={handleSelectCuratedWithScroll}
                      canAddBrowseAll={canAddBrowseAll}
                      experiencesArray={experiencesArray}
                      insertionIndex={index + 1}
                    />
                  </div>
                ))}
              </div>
            </UnifiedDragProvider>
          </div>
        </LearningCollectionDetailsProvider>
      );
  }
}

export default observer(SectionsLoader);
